/**
 * useFeatureFlags — org feature-flag admin data (Phase 4/5).
 * Wraps /v1/rbac/feature-flags.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { config } from '../config';

export interface FeatureFlag {
  featureKey: string;
  enabled: boolean;
}

export const flagKeys = { all: ['rbac', 'feature-flags'] as const };

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export function useFeatureFlags() {
  return useQuery<FeatureFlag[], Error>({
    queryKey: flagKeys.all,
    queryFn: async () => {
      const res = await fetch(`${config.API_URL}/v1/rbac/feature-flags`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to load feature flags (${res.status})`);
      return res.json();
    },
  });
}

export function useSetFeatureFlag() {
  const qc = useQueryClient();
  return useMutation<FeatureFlag, Error, { featureKey: string; enabled: boolean }>({
    mutationFn: async ({ featureKey, enabled }) => {
      const res = await fetch(
        `${config.API_URL}/v1/rbac/feature-flags/${featureKey}`,
        { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ enabled }) },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.detail || `Failed to update flag (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: flagKeys.all });
      // Toggling a feature changes the org's capabilities — refresh the access
      // profile too, or useAccess serves stale caps for its staleTime: a
      // disabled feature stays clickable (then 403s), an enabled one stays
      // hidden until reload.
      qc.invalidateQueries({ queryKey: ['rbac', 'me'] });
    },
  });
}
